import type { BlockDefinition } from "./blockRegistry.ts";

export interface MinecraftBlockCatalogEntry {
  id: string;
  stateOptions: Record<string, string[]>;
  defaultState: Record<string, string>;
}

const TECHNICAL_BLOCKS = new Set([
  "air",
  "cave_air",
  "void_air",
  "moving_piston",
  "piston_head",
  "nether_portal",
  "end_portal",
  "end_gateway",
  "fire",
  "soul_fire"
]);

const DEFAULT_VALUE_PREFERENCES: Record<string, string[]> = {
  axis: ["y", "x", "z"],
  facing: ["north", "south", "east", "west", "up", "down"],
  horizontal_facing: ["north", "south", "east", "west"],
  half: ["bottom", "lower", "top", "upper"],
  type: ["bottom", "single", "top", "double"],
  shape: ["straight", "north_south", "east_west", "inner_left", "inner_right"],
  hinge: ["left", "right"],
  open: ["false", "true"],
  powered: ["false", "true"],
  waterlogged: ["false", "true"],
  lit: ["false", "true"],
  enabled: ["true", "false"],
  persistent: ["true", "false"],
  distance: ["7", "1", "2", "3", "4", "5", "6"],
  snowy: ["false", "true"],
  hanging: ["false", "true"],
  up: ["true", "false"],
  north: ["false", "none", "low", "true", "tall"],
  east: ["false", "none", "low", "true", "tall"],
  south: ["false", "none", "low", "true", "tall"],
  west: ["false", "none", "low", "true", "tall"]
};

function titleFromId(id: string): string {
  return id
    .split("_")
    .map((part) => part ? part[0].toUpperCase() + part.slice(1) : part)
    .join(" ");
}

function colorFromId(id: string): string {
  let hash = 2166136261;
  for (const character of id) {
    hash ^= character.charCodeAt(0);
    hash = Math.imul(hash, 16777619);
  }

  const hue = Math.abs(hash) % 360;
  const saturation = 24 + (Math.abs(hash >>> 8) % 28);
  const lightness = 38 + (Math.abs(hash >>> 16) % 28);
  return `hsl(${hue} ${saturation}% ${lightness}%)`;
}

function collectConditionValues(
  condition: unknown,
  accumulator: Map<string, Set<string>>
): void {
  if (!condition || typeof condition !== "object") return;

  if (Array.isArray(condition)) {
    condition.forEach((entry) => collectConditionValues(entry, accumulator));
    return;
  }

  const record = condition as Record<string, unknown>;
  for (const [key, value] of Object.entries(record)) {
    if (key === "OR" || key === "AND") {
      collectConditionValues(value, accumulator);
      continue;
    }

    const values = String(value)
      .split("|")
      .map((entry) => entry.trim())
      .filter(Boolean);
    if (!accumulator.has(key)) accumulator.set(key, new Set());
    values.forEach((entry) => accumulator.get(key)!.add(entry));
  }
}

function collectVariantValues(
  variants: Record<string, unknown> | undefined,
  accumulator: Map<string, Set<string>>
): void {
  if (!variants) return;

  Object.keys(variants).forEach((variantKey) => {
    if (!variantKey) return;

    variantKey.split(",").forEach((pair) => {
      const separator = pair.indexOf("=");
      if (separator <= 0) return;
      const key = pair.slice(0, separator).trim();
      const values = pair
        .slice(separator + 1)
        .split("|")
        .map((value) => value.trim())
        .filter(Boolean);

      if (!accumulator.has(key)) accumulator.set(key, new Set());
      values.forEach((value) => accumulator.get(key)!.add(value));
    });
  });
}

function chooseDefaultValue(key: string, values: string[]): string {
  const preferred = DEFAULT_VALUE_PREFERENCES[key] ?? [];
  for (const value of preferred) {
    if (values.includes(value)) return value;
  }
  return values[0] ?? "";
}

export function inferBlockStateSchema(
  blockStateDefinition: Record<string, unknown>
): Pick<MinecraftBlockCatalogEntry, "stateOptions" | "defaultState"> {
  const values = new Map<string, Set<string>>();

  collectVariantValues(
    blockStateDefinition.variants as Record<string, unknown> | undefined,
    values
  );

  const multipart = blockStateDefinition.multipart;
  if (Array.isArray(multipart)) {
    multipart.forEach((part) => {
      if (part && typeof part === "object") {
        collectConditionValues(
          (part as Record<string, unknown>).when,
          values
        );
      }
    });
  }

  const stateOptions: Record<string, string[]> = {};
  const defaultState: Record<string, string> = {};

  [...values.entries()]
    .sort(([first], [second]) => first.localeCompare(second))
    .forEach(([key, valueSet]) => {
      const options = [...valueSet];
      stateOptions[key] = options;
      defaultState[key] = chooseDefaultValue(key, options);
    });

  return { stateOptions, defaultState };
}

export function shouldExposeBlock(blockId: string): boolean {
  return !TECHNICAL_BLOCKS.has(blockId);
}

export function inferConnectionType(
  blockId: string
): BlockDefinition["connectionType"] | undefined {
  if (blockId === "iron_bars" || blockId.endsWith("_pane")) return "pane";
  if (blockId.endsWith("_fence")) return "fence";
  if (blockId.endsWith("_wall")) return "wall";
  return undefined;
}

export function inferPlacementType(
  blockId: string
): BlockDefinition["placementType"] | undefined {
  if (blockId.endsWith("_door") && !blockId.endsWith("_trapdoor")) {
    return "door";
  }
  if (blockId.endsWith("_bed") || blockId === "bed") {
    return "bed";
  }
  return undefined;
}

export function createGenericBlockDefinition(
  blockId: string,
  minecraftVersion: string,
  blockStateDefinition: Record<string, unknown>
): BlockDefinition {
  const { stateOptions, defaultState } =
    inferBlockStateSchema(blockStateDefinition);
  const color = colorFromId(blockId);
  const name = titleFromId(blockId);

  let placementType = inferPlacementType(blockId);
  if (
    !placementType &&
    stateOptions.half?.includes("lower") &&
    stateOptions.half?.includes("upper")
  ) {
    placementType = "double_plant";
  }

  const transparent =
    blockId.includes("glass") ||
    blockId.includes("pane") ||
    blockId.includes("leaves") ||
    blockId.includes("door") ||
    blockId.includes("trapdoor") ||
    blockId.includes("bars") ||
    blockId.includes("flower") ||
    blockId.includes("sapling") ||
    blockId.includes("mushroom") ||
    blockId.includes("grass") ||
    blockId.includes("fern") ||
    blockId.includes("vine");

  return {
    id: blockId,
    minecraftName: "minecraft:" + blockId,
    name,
    shortName: name.length > 9 ? name.slice(0, 8) + "…" : name,
    color,
    palette: [color],
    pattern: "stone",
    transparent,
    opacity: transparent ? 1 : undefined,
    alphaTest: transparent ? 0.2 : undefined,
    defaultState,
    stateOptions,
    connectionType: inferConnectionType(blockId),
    placementType,
    availableInVersion: minecraftVersion,
    category: inferBlockCategory(blockId)
  };
}

export function inferBlockCategory(blockId: string): string {
  if (
    blockId.includes("log") ||
    blockId.includes("wood") ||
    blockId.includes("planks") ||
    blockId.includes("fence") ||
    blockId.includes("door") ||
    blockId.includes("trapdoor")
  ) return "Wood";

  if (
    blockId.includes("stone") ||
    blockId.includes("brick") ||
    blockId.includes("deepslate") ||
    blockId.includes("tuff") ||
    blockId.includes("cobble") ||
    blockId.includes("sandstone")
  ) return "Stone";

  if (
    blockId.includes("leaves") ||
    blockId.includes("flower") ||
    blockId.includes("sapling") ||
    blockId.includes("mushroom") ||
    blockId.includes("grass") ||
    blockId.includes("shrub") ||
    blockId.includes("moss")
  ) return "Nature";

  if (
    blockId.includes("glass") ||
    blockId.includes("wool") ||
    blockId.includes("concrete") ||
    blockId.includes("terracotta") ||
    blockId.includes("carpet")
  ) return "Color & Decor";

  if (
    blockId.includes("redstone") ||
    blockId.includes("repeater") ||
    blockId.includes("comparator") ||
    blockId.includes("piston") ||
    blockId.includes("observer") ||
    blockId.includes("rail")
  ) return "Redstone";

  return "Building";
}
